import React from 'react'
// import './style/org-notification.scss'

import { OrgNotificationModel } from '../../models/notification/org-notification.model'
import moment from 'moment-timezone'
import { BASE_TIME_FORMAT, BASE_TIME_FORMAT_WITHOUT_TZ } from '../../constants'
import { NotificationDeliveryWindow } from '../../enums/notification-delivery-window'
import { NotificationDeliveryType } from '../../enums/notification-delivery-type'
import { arrayUniqueObjects, titleCase } from '../../_utils/utils'
import { MultiDomainBadge } from '../../components/badges/multi-domain-badge'
import { NotificationImageBadge } from '../../components/badges/notification-image-badge'
import { NotificationSilentBadge } from '../../components/badges/notification-silent-badge'
import { NotificationStzBadge } from '../../components/badges/notification-stz-badge'
import { generateShortID } from '../../_utils/uid'

import { ApartmentOutlined, AndroidFilled, AppleFilled, ChromeFilled, GlobalOutlined } from '@ant-design/icons'
import { getPathEntityId } from '../../_utils/get-path-entity-id'
import OrgNotificationLoader from './helpers/org-notification-loader'
import { ILoadableDataState } from '../../hooks/use-loadable-data-ref-state'
import OrgLoader from './helpers/org-loader'
import { AccountDto } from '../../dtos/account.dto'
import { TabbedView } from '../../components/tabbed-view/tabbed-view'
import SimpleTabbedView from '../../components/simple-tabbed-view/simple-tabbed-view'
import OrgNotificationSummaryTab from './org-notification-summary-tab'
import { useService } from '../../hooks/use-service'
import { AppState } from '../../stores/app'
import { flatMap } from '../../_utils/array'
import { determineChannelBadge } from '../../components/badges/_utils'
import { NoTranslate } from '../../components/no-translate/no-translate'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { Skeleton, Tag } from 'antd'
import { PushlyAi } from '@pushly/aqe/lib/components'
import {
    DetailsSummaryWell,
    DetailsSummarySection,
    DetailsSummarySubSections,
    DetailsSummaryField,
} from '@pushly/aqe/lib/components/details-summary-well'
import { NotificationStatusBadge } from '../../components/badges/notification-status-badge'
import { NotificationVariantModel } from '../../models/notification/notification-variant.model'
import { NotificationDefaultContentModel } from '../../models/notification/notification-default-content.model'
import { NotificationExperienceModel } from '../../models/notification/notification-experience.model'

interface IHeaderProps {
    loader: React.MutableRefObject<ILoadableDataState<OrgNotificationModel | undefined>>
    org: AccountDto | undefined
}

function renderOrgChannelValues(
    build: NotificationVariantModel | undefined,
    org: AccountDto | undefined,
    appState: AppState,
): React.ReactNode {
    const channels = build?.getChannels()
    if (!channels) return undefined
    const viewHasNativePush = org?.isIOSEnabled(appState) || org?.isAndroidEnabled(appState)

    const filtered = channels.filter((val) => (!viewHasNativePush ? val === DeliveryChannel.WEB : true))

    const items: React.ReactNode[] = []
    if (filtered.includes(DeliveryChannel.WEB))
        items.push(
            <span key="web">
                <ChromeFilled /> Web
            </span>,
        )
    if (filtered.includes(DeliveryChannel.NATIVE_IOS))
        items.push(
            <span key="ios">
                <AppleFilled /> Native Apple
            </span>,
        )
    if (filtered.includes(DeliveryChannel.NATIVE_ANDROID))
        items.push(
            <span key="android">
                <AndroidFilled /> Native Android
            </span>,
        )

    if (items.length === 0) return undefined

    return items.reduce<React.ReactNode[]>((acc, item, i) => {
        if (i > 0) acc.push(', ')
        acc.push(item)
        return acc
    }, [])
}

function renderOrgBehaviorsField(expOpts: NotificationExperienceModel | undefined): React.ReactNode {
    const behaviors: string[] = []

    if (expOpts?.getIsSilent()) behaviors.push('Silent Delivery')
    if (expOpts?.getRequireInteraction()) behaviors.push('Requires Interaction')

    if (behaviors.length === 0) return null
    return <DetailsSummaryField label="Behaviors" value={behaviors.join(', ')} />
}

function renderOrgContentSection(content: NotificationDefaultContentModel | undefined): React.ReactNode {
    const aiResults = content?.getAutoSuggestedFieldsResults()

    let aiSource = aiResults?.source
    if (
        aiResults &&
        !aiSource &&
        (aiResults.title || aiResults.body || aiResults.imageUrl || aiResults.keywords?.length)
    ) {
        aiSource = 'page_analyzer'
    }

    if (!aiSource) return null

    const isPushlyAi = aiSource === 'pushly_ai'
    const edited = (aiResults as any)?.edited === true
    const tone = aiResults?.source_metadata?.tone ?? aiResults?.sourceMetadata?.tone ?? aiResults?.tone
    const reason = aiResults?.source_metadata?.reason ?? aiResults?.sourceMetadata?.reason ?? aiResults?.reason
    const displayName = (isPushlyAi ? 'Pushly AI' : 'Page Analyzer') + (edited ? ' (Edited)' : '')

    return (
        <DetailsSummarySection title="Content">
            <DetailsSummaryField
                label="Content Source"
                value={
                    <>
                        {isPushlyAi && <PushlyAi />} {displayName}
                    </>
                }
            />
            {tone && <DetailsSummaryField label="AI Content Tone" value={<Tag>{titleCase(tone)}</Tag>} />}
            {reason && <DetailsSummaryField label="AI Content Reason" value={reason} />}
        </DetailsSummarySection>
    )
}

const OrgNotificationDetailsHeader = ({ loader, org }: IHeaderProps) => {
    const appState = useService<AppState>(AppState)

    const notif = loader.current.data
    const loading = loader.current.loading || !notif
    const build = notif?.getVariant()
    const content = build?.getContent().getDefaultContent()
    const expOpts = content?.getExperienceOptions()
    const delivery = build?.getDelivery()

    // delivery strategy
    const type = delivery?.getType()
    const window = delivery?.getWindow()

    let strategyStr: string = ''
    if (window === NotificationDeliveryWindow.INFORMED) {
        strategyStr = 'informed'
    } else if (type === NotificationDeliveryType.IMMEDIATE) {
        strategyStr = 'immediate'
    } else if (window === NotificationDeliveryWindow.STANDARD) {
        strategyStr = 'fixed'
    } else if (window === NotificationDeliveryWindow.TIMEZONE) {
        strategyStr = 'subscriber time zone'
    }

    // delivery dates
    const isSTZ = delivery?.isUsingStzStrategy ?? false
    const sendDateFormat = isSTZ ? `${BASE_TIME_FORMAT_WITHOUT_TZ} [STZ]` : BASE_TIME_FORMAT

    const startDate = moment.tz(delivery?.getSendDate(), delivery?.getTimeZone() ?? org?.timezone)
    const expiryDate = startDate.clone().add(expOpts?.getTtlSeconds(), 'seconds')

    const flightDateStart = startDate.format(isSTZ ? `${BASE_TIME_FORMAT_WITHOUT_TZ} [STZ]` : BASE_TIME_FORMAT)
    const flightDateEnd = expiryDate.format(sendDateFormat)

    // audience
    let domainsView: React.ReactNode
    let excludedSegmentsView: React.ReactNode
    let segmentsView: React.ReactNode = (
        <Tag key="all-subs" variant="outlined">
            All Subscribers
        </Tag>
    )
    const audience = notif?.getVariant()?.getAudience()
    const currUserDomains = appState.currentUserDomains ?? []

    if (audience?.getDomainIds()) {
        const domainIds = audience.getDomainIds()!.map((id) => id.toString())
        const audienceDomains = currUserDomains.filter((d) => domainIds.includes(d.id.toString()))
        domainsView = audienceDomains.map((domain) => (
            <Tag key={domain.id} variant="outlined">
                {domain.displayName}
            </Tag>
        ))
    }

    if (audience?.getSegmentIds()) {
        const segments = arrayUniqueObjects(
            flatMap(notif!.getNotifications()!, (n) => n.includedSegments ?? []),
            'groupId',
        )
        segmentsView = segments.map((segment: any) => (
            <Tag key={segment.id} variant="outlined">
                {segment.groupName ?? segment.name}
            </Tag>
        ))
    }

    if (audience?.getExcludedSegmentIds()?.length) {
        const excludedSegments = arrayUniqueObjects(
            flatMap(notif!.getNotifications()!, (n) => n.excludedSegments ?? []),
            'groupId',
        )
        if (excludedSegments.length) {
            excludedSegmentsView = excludedSegments.map((segment: any) => (
                <Tag key={segment.groupId} variant="outlined">
                    {segment.groupName ?? segment.name}
                </Tag>
            ))
        }
    }

    // status
    const computedStatus = notif?.getComputedStatus()
    const isHoldOut = delivery?.getWindow() === NotificationDeliveryWindow.HOLD_OUT

    return (
        <div className="details-header">
            <div className="notif-title">
                <h2>{content?.getTitle()}</h2>
                <div className="append">
                    <div className="notif-id">
                        <span>
                            #{notif?.getId()}{' '}
                            <span>
                                | Created by <NoTranslate>{notif?.getCreatedByUserName()}</NoTranslate>
                            </span>
                        </span>
                    </div>
                </div>
            </div>

            <DetailsSummaryWell loading={loading}>
                <DetailsSummarySection title="General">
                    <DetailsSummaryField label="ID" value={notif?.getId()} copyable />
                    <DetailsSummaryField
                        label="Status"
                        value={
                            computedStatus && (
                                <NotificationStatusBadge status={computedStatus} expanded={true} holdOut={isHoldOut} />
                            )
                        }
                    />
                    <DetailsSummaryField label="Channels" value={renderOrgChannelValues(build, org, appState)} />
                    <DetailsSummaryField
                        label="Scope"
                        value={
                            notif?.getIsMultiDomain() ? (
                                <span>
                                    <ApartmentOutlined /> Multi-Domain
                                </span>
                            ) : (
                                <span>
                                    <GlobalOutlined /> Single Domain
                                </span>
                            )
                        }
                    />
                </DetailsSummarySection>

                <DetailsSummarySubSections>
                    <DetailsSummarySection title="Delivery">
                        <DetailsSummaryField
                            label="Delivery Date Range"
                            value={
                                <>
                                    {flightDateStart} <span style={{ opacity: 0.5 }}>through</span>
                                    <br />
                                    {flightDateEnd}
                                </>
                            }
                        />
                        <DetailsSummaryField
                            label="Delivery Strategy"
                            value={strategyStr ? titleCase(strategyStr) : undefined}
                        />
                        {renderOrgBehaviorsField(expOpts)}
                    </DetailsSummarySection>

                    <DetailsSummarySection title="Targeting">
                        <DetailsSummaryField label="Domains" value={domainsView} />
                        <DetailsSummaryField label="Targeted Audience(s)" value={segmentsView} />
                        {!!excludedSegmentsView && (
                            <DetailsSummaryField label="Excluded Audience(s)" value={excludedSegmentsView} />
                        )}
                    </DetailsSummarySection>
                </DetailsSummarySubSections>

                {renderOrgContentSection(content)}
            </DetailsSummaryWell>
        </div>
    )
}

class OrgNotification extends TabbedView<any, any> {
    public render() {
        const orgId = getPathEntityId('organization')
        const notifId = getPathEntityId('notification')

        return (
            <div className="notification-details org-notification-details page">
                <OrgLoader orgId={orgId!}>
                    {(orgLoader) => (
                        <OrgNotificationLoader
                            orgId={orgId!}
                            notifId={notifId!.toString()}
                            stats={true}
                            includeCancelledChildren={true}
                        >
                            {(loader) => (
                                <>
                                    <OrgNotificationDetailsHeader loader={loader} org={orgLoader.current.data} />
                                    <SimpleTabbedView
                                        pathStyle="slash"
                                        animated={false}
                                        tabs={[
                                            {
                                                component: OrgNotificationSummaryTab,
                                                props: () => ({
                                                    notifLoader: loader,
                                                    orgLoader,
                                                }),
                                            },
                                        ]}
                                    />
                                </>
                            )}
                        </OrgNotificationLoader>
                    )}
                </OrgLoader>
            </div>
        )
    }
}

export default OrgNotification
